import _ from 'lodash';

import {
  ADVANCE_MODULE,
  BASE_MODULE,
  BASIC_FIELD,
  IX_ONE_FIELD,
  TDC_PROPERTIES,
  PRODUCT_SPEC,
  LABELING,
  CLAIMS,
  PET_FOOD,
  FRESH_SEAFOOD,
  FACT_PANEL,
  NUTRITION,
  SUPPLEMENT,
  PET_NUTRITION,
  CERTIFICATIONS_MODULE,
  SPECIAL_DELETED_GROUP,
  ALLERGEN_DELETED_FIELD,
  INGREDIENTS_DELETED_FIELD,
  CERTIFICATION_LABELING,
} from './IXONEConstants';

import { checkSpecialField } from 'pages/product-full-view/components/product-new-detail-view/utils';

import {
  SUPPLEMENT_TYPE,
  NUTRITION_TYPE,
  PET_NUTRITION_TYPE,
} from 'pages/product-full-view/components/product-media-area/multiple-panel/constants';
import { PRODUCT_IMAGERY_LABELS_BY_LEVEL } from './components/constants';

const SUBTYPE_FIELD_MAP = {
  [NUTRITION_TYPE]: NUTRITION,
  [SUPPLEMENT_TYPE]: SUPPLEMENT,
  [PET_NUTRITION_TYPE]: PET_NUTRITION,
};

const isDeleteCurrentCertificationsValue = (currentValue, originalValue) => {
  if (typeof currentValue === 'boolean') return !currentValue;
  else {
    if (currentValue?.length === originalValue?.length) {
      return currentValue.some((item) => !originalValue.includes(item));
    }
    return (
      !currentValue?.length || currentValue?.length < originalValue?.length
    );
  }
};

const isChangedCertificationField = (field, originalData) => {
  const currentCerValue = field?.value;
  const originalValue = _.get(originalData, field?.key, null);
  const isDeleteCurrentValue = isDeleteCurrentCertificationsValue(
    currentCerValue,
    originalValue
  );

  const isChanged =
    JSON.stringify(originalValue) !== JSON.stringify(currentCerValue) &&
    isDeleteCurrentValue;

  return isChanged;
};

export const checkDetailFieldsChange = ({ originalData, changedFields }) => {
  const TDC_FIELD = {
    ...TDC_PROPERTIES,
    ...BASE_MODULE,
    ...ADVANCE_MODULE,
    ...BASIC_FIELD,
  };

  let list = [];
  let isChanged = false;

  for (const [, field] of Object.entries(changedFields)) {
    if (!TDC_FIELD?.[field?.fieldFullPath?.toLowerCase()]) continue;

    if (field?.fieldFullPath?.toLowerCase() in CERTIFICATIONS_MODULE) {
      if (isChangedCertificationField(field, originalData)) {
        isChanged = true;
        list = [...new Set([...list, field?.fieldFullPath])];
      }
    } else {
      const originalValue = _.get(originalData, field?.key, null);
      const currentValue = field?.value ?? null;

      if (
        JSON.stringify(originalValue) !== JSON.stringify(currentValue) &&
        originalValue &&
        !IX_ONE_FIELD?.includes(field?.fieldFullPath?.toLowerCase())
      ) {
        isChanged = true;
        list = [...new Set([...list, field?.fieldFullPath])];
      }
      //* end for
    }
  }

  return {
    list,
    result: isChanged,
  };
};

const GROUP_FACT_PANEL = {
  nutritionfactproperties: 'nutrition Fact Properties',
  nutrients: 'nutrients',
  dietaryingredients: 'Dietary Ingredients',
};

export const checkFieldsChange = ({
  changedFields,
  originalData,
  subType = null,
}) => {
  const SUBTYPE_FIELD = SUBTYPE_FIELD_MAP[subType] || {};

  const productImage =
    PRODUCT_IMAGERY_LABELS_BY_LEVEL[originalData?.packageLevel || 'Unit'];

  const productImageLowerCase = lowerCaseFieldName(productImage);

  const TDC_FIELD = {
    ...TDC_PROPERTIES,
    ...BASE_MODULE,
    ...ADVANCE_MODULE,
    ...BASIC_FIELD,
    ...LABELING,
    ...SUBTYPE_FIELD,
    ...productImageLowerCase,
  };

  const keysData = Object.keys(changedFields)?.filter(
    (key) => !IX_ONE_FIELD.includes(key?.toLowerCase())
  );

  const list = keysData.reduce((acc, field) => {
    const originalValue = originalData?.[field] ?? null;
    const currentValue = changedFields?.[field] ?? null;

    let hasChange = subType
      ? originalValue && !currentValue
      : JSON.stringify(originalValue) !== JSON.stringify(currentValue) &&
        originalValue;

    if (subType && field?.toLowerCase() in GROUP_FACT_PANEL) {
      hasChange = originalValue?.length > currentValue?.length;

      if (!hasChange) return acc;

      acc.push(field);
      return acc;
    }

    if (!subType && field?.toLowerCase() in CERTIFICATION_LABELING) {
      const originalValueFilter = originalValue?.filter(Boolean);
      const currentValueFilter = currentValue?.filter(Boolean);
      hasChange = originalValueFilter?.length > currentValueFilter?.length;

      if (!hasChange) return acc;

      acc.push(field);
      return acc;
    }

    if (!hasChange) return acc;

    if (Boolean(TDC_FIELD[field?.toLowerCase()])) {
      acc.push(field);
      //* break the loop, to continue for checking nested, DO NOT add the field name into TDC_FIELD
      return acc;
    }
    //* check nested field in object value
    if (_.isObject(currentValue) || _.isObject(originalValue))
      checkNestedFieldChange({
        list: acc,
        currentData: currentValue,
        originalData: originalValue,
        parentField: [field],
        TDC_FIELD,
      });

    return acc;
  }, []);

  return {
    list,
    result: list?.length > 0,
  };
};

const checkNestedFieldChange = ({
  list,
  currentData,
  originalData,
  parentField,
  TDC_FIELD,
}) => {
  if (currentData?.length || originalData?.length) {
    checkNestedListFieldChange({
      currentData,
      originalData,
      parentField,
      list,
      TDC_FIELD,
    });
    return;
  }

  //* handle when checkValue is object
  Object.keys(currentData).forEach((currentField) => {
    const fieldFullPathArray = [...parentField, currentField];
    const fieldFullPath = fieldFullPathArray.join('.');

    const originalValue = originalData?.[currentField] ?? null;
    const currentValue = currentData?.[currentField] ?? null;

    const hasChange =
      JSON.stringify(originalValue) !== JSON.stringify(currentValue);

    if (!hasChange) return;

    const isTdcField = TDC_FIELD[fieldFullPath?.toLowerCase()];
    if (isTdcField) {
      list.push(fieldFullPath);
    }

    //* recursive - check nested field in object value
    if (_.isObject(currentValue) || _.isObject(originalValue)) {
      checkNestedFieldChange({
        list,
        currentData: currentValue,
        originalData: originalValue,
        parentField: fieldFullPathArray,
        TDC_FIELD,
      });
    }
  });
};

const checkNestedListFieldChange = ({
  currentData,
  originalData,
  parentField,
  list,
  TDC_FIELD,
}) => {
  let loopValue = currentData;
  let checkValue = originalData;

  //* in case, current value is object list but it is empty, use original value to check
  if (!currentData?.length && originalData?.length) {
    loopValue = originalData;
    checkValue = currentData;
  }

  return loopValue.forEach((value, index) => {
    const fieldArray = [...parentField];

    const currentValue = value;
    const originalValue = checkValue?.[index];

    if (_.isObject(currentValue) || _.isObject(originalValue))
      checkNestedFieldChange({
        list,
        currentData: currentValue,
        originalData: originalValue,
        parentField: fieldArray,
        TDC_FIELD,
      });
  });
};

export const checkFieldsChangeBulkEdit = (changedModule, originalData) => {
  const TDC_FIELD = {
    ...TDC_PROPERTIES,
    ...BASE_MODULE,
    ...ADVANCE_MODULE,
    ...BASIC_FIELD,
    ...LABELING,
    ...FACT_PANEL,
  };

  let changedFields = [];
  changedModule?.forEach((module) => {
    changedFields = [...changedFields, ...module?.columns];
  });

  const removedIgnoreField = changedFields?.filter((item) => {
    return (
      !IX_ONE_FIELD?.includes(item?.toLowerCase()) && !checkSpecialField(item)
    );
  });

  const hasTdcField = removedIgnoreField.some((field) =>
    Boolean(TDC_FIELD[field?.toLowerCase()])
  );

  return { result: hasTdcField && originalData?.length > 0, list: [] };
};

export const lowerCaseFieldName = (object) => {
  return Object.fromEntries(
    Object.entries(object || {}).map(([key, value]) => [
      key.toLowerCase(),
      value,
    ])
  );
};

export const normalizeEmptyValue = (value) => {
  if (value === 0 || value === false) {
    return value;
  }

  if (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (_.isPlainObject(value) && Object.keys(value).length === 0)
  ) {
    return null;
  }

  return value;
};
